import { apiClient } from '@/config/api';
import { User, HydraCollection, PaginatedResponse } from '@/types';

export interface CreateUserData {
  email: string;
  fullName: string;
  password: string;
  roles: string[];
  phone?: string;
  isActive?: boolean;
}

export interface UpdateUserData {
  email?: string;
  fullName?: string;
  password?: string;
  roles?: string[];
  phone?: string;
  isActive?: boolean;
}

export interface GetUsersParams {
  page?: number;
  itemsPerPage?: number;
  search?: string;
}

export const userService = {
  // Get all users with pagination and search
  async getUsers(params?: GetUsersParams): Promise<PaginatedResponse<User>> {
    const page = params?.page || 1;
    const itemsPerPage = params?.itemsPerPage || 20;
    const search = params?.search;

    const requestParams: Record<string, unknown> = {
      'groups[]': ['user:read'],
      page: page,
      itemsPerPage: itemsPerPage,
    };

    // Add search parameter if provided
    if (search && search.trim() !== '') {
      requestParams['email'] = search;
    }

    const response = await apiClient.get<HydraCollection<User>>('/users', {
      params: requestParams,
    });

    const data = response.data;
    const users = (data['hydra:member'] || data['member'] || []).map((user: User) => {
      // Convert isActive to boolean explicitly
      let isActiveValue: boolean;
      if (user.isActive === true || user.isActive === 1 || user.isActive === '1' || user.isActive === 'true') {
        isActiveValue = true;
      } else {
        // Default to false for: false, 0, "0", "false", null, undefined, etc.
        isActiveValue = false;
      }
      
      return {
        ...user,
        isActive: isActiveValue,
      };
    });
    const totalItems = data['hydra:totalItems'] || data['totalItems'] || 0;
    const view = data['hydra:view'] || data['view'];

    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const hasNextPage = !!view?.['hydra:next'];
    const hasPreviousPage = !!view?.['hydra:previous'];

    return {
      data: users,
      totalItems,
      currentPage: page,
      itemsPerPage,
      totalPages,
      hasNextPage,
      hasPreviousPage,
    };
  },

  // Get single user
  async getUser(id: number): Promise<User> {
    const response = await apiClient.get(`/users/${id}`, {
      params: {
        'groups[]': ['user:read'],
      },
    });
    const user = response.data;
    // Convert isActive to boolean explicitly
    let isActiveValue: boolean;
    if (user.isActive === true || user.isActive === 1 || user.isActive === '1' || user.isActive === 'true') {
      isActiveValue = true;
    } else {
      // Default to false for: false, 0, "0", "false", null, undefined, etc.
      isActiveValue = false;
    }
    
    return {
      ...user,
      isActive: isActiveValue,
    };
  },

  // Create user
  async createUser(data: CreateUserData): Promise<User> {
    const response = await apiClient.post('/users', {
      ...data,
      'groups[]': ['user:create'],
    });
    return response.data;
  },

  // Update user
  async updateUser(id: number, data: UpdateUserData): Promise<User> {
    const response = await apiClient.put(`/users/${id}`, data);
    return response.data;
  },

  // Delete user
  async deleteUser(id: number): Promise<void> {
    await apiClient.delete(`/users/${id}`);
  },

  // Change password without replacing the whole user resource
  async changePassword(userId: number, newPassword: string): Promise<void> {
    await apiClient.patch(
      `/users/${userId}`,
      {
        password: newPassword,
      },
      {
        headers: {
          'Content-Type': 'application/merge-patch+json',
        },
      }
    );
  },
};
